import t from "@/lang/lang";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  PatchPositionRequest,
  PositionPatch,
  updateHtzWorkOrderPositionCache,
  useHtzWorkOrderAssignPositionAhuMutation,
  useHtzWorkOrderListPositionQuery,
  useHtzWorkOrderPatchPositionMutation,
  useHtzWorkOrderShowQuery,
  workOrderPositionsRequest,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { Fragment, useEffect, useMemo } from "react";
import { NullUUID, UUID } from "@bitory-gmbh/nidavellir";
import {
  useHtzAirHandlingUnitListQuery,
  useHtzAirHandlingUnitShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-api";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellCheckbox,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  useFieldErrors,
  useHasFieldErrors,
  useErrValidationFailedContext,
  useResetFieldsOnChange,
} from "@/shared/lib/errors/err-validation-failed-context";
import { FieldErrors } from "@/shared/components/field/field";
import { cn } from "@/shared/lib/utils";
import { InspectionSheet } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/inspection-sheet";
import { DeletePositionDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/delete-position-dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import {
  Position,
  positionFieldName,
  updateUnitCount,
  updateUnitHours,
  updateUnitPrice,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { TextareaField } from "@/shared/components/field/textarea-field";
import {
  DecimalNumberInputField,
  NumberInputField,
} from "@/shared/components/field/input-field";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";

/*
 * WorkOrderPositionsTable renders the positions of
 * a work order and saves the edits of a row as they
 * are made.
 */
export function WorkOrderPositionsTable({
  workOrderId,
}: {
  workOrderId: string;
}) {
  const { data, isLoading, error } = useHtzWorkOrderListPositionQuery(
    workOrderPositionsRequest(workOrderId),
  );
  const positions = data?.items;

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Pos")}</ETableHead>
          <ETableHead>{t("Erledigt")}</ETableHead>
          <ETableHead>{t("RLT-Anlage")}</ETableHead>
          <ETableHead>{t("Titel")}</ETableHead>
          <ETableHead>{t("Optional?")}</ETableHead>
          <ETableHead colSpan={3}>{t("Aktionen")}</ETableHead>
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {isLoading && <TableLoadingRow colSpan={8} />}
        {error !== undefined && <TableRtkErrorRow colSpan={8} error={error} />}
        {positions && positions.length === 0 && <TableEmptyRow colSpan={8} />}
        {positions && positions.length !== 0 && (
          <SplitPositionsByAhu positions={positions} />
        )}
      </ETableBody>
    </ETable>
  );
}

function SplitPositionsByAhu({ positions }: { positions: Position[] }) {
  // We collect the ahu ids in the positions to be able
  // to render the positions grouped by ahu id.
  const ahuIds = useMemo(
    () =>
      positions.reduce((prev, curr) => {
        // Positions without a ahu id are
        // grouped by a null UUID.
        let ahuId: UUID;
        if (!curr.ahuId) {
          ahuId = NullUUID;
        } else {
          ahuId = curr.ahuId;
        }

        // Only add the id if it does not exist
        if (prev.some((p) => p === ahuId)) {
          return prev;
        }

        return [...prev, ahuId];
      }, [] as UUID[]),
    [positions],
  );

  return (
    <>
      {ahuIds.map((ahuId) => (
        <Fragment key={ahuId}>
          <ETableRow>
            <ETableCell colSpan={8} className="bg-muted h-8 py-0 font-medium">
              {ahuId === NullUUID ? (
                t("Anlagenübergreifend")
              ) : (
                <AhuName ahuId={ahuId} />
              )}
            </ETableCell>
          </ETableRow>
          {positions?.filter(byAhuId(ahuId)).map((position) => (
            <PositionTableRow key={position.id} position={position} />
          ))}
        </Fragment>
      ))}
    </>
  );
}

function AhuName({ ahuId }: { ahuId: string }) {
  const { data: airHandlingUnits } = useHtzAirHandlingUnitShowQuery({
    id: ahuId,
  });

  return <span>{airHandlingUnits?.name}</span>;
}

function byAhuId(ahuId: string) {
  return (a: Position) => {
    if (!a.ahuId) {
      return NullUUID === ahuId;
    }
    return a.ahuId === ahuId;
  };
}

function PositionTableRow({ position }: { position: Position }) {
  const { data: workOrder } = useHtzWorkOrderShowQuery({
    id: position.workOrderId,
  });
  const request: PatchPositionRequest = {
    id: position.workOrderId,
    positionId: position.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzWorkOrderPositionCache,
    useHtzWorkOrderPatchPositionMutation(),
  );

  // Will become available eventually.
  if (!workOrder) return null;

  const disabled = isImmutable(workOrder);

  return (
    <ETableRow>
      <ETableCell className="text-center">{position.number}</ETableCell>
      <ETableCellCheckbox
        checked={position.completed}
        onCheckedChange={() =>
          set(
            { ...request, completed: !position.completed },
            { ...position, completed: !position.completed },
          )
        }
        disabled={disabled}
        label={position.completed ? t("ja") : t("nein")}
      />
      <AhuSelectCell position={position} workOrder={workOrder} />
      <ETableCellInput
        value={position.title}
        onChange={(e) =>
          set(
            { ...request, title: e.target.value },
            { ...position, title: e.target.value },
          )
        }
        disabled={disabled}
        fieldName={positionFieldName(position, "title")}
      />
      <ETableCellCheckbox
        checked={position.optional}
        label={position.optional ? t("ja") : t("nein")}
        disabled
      />
      <ETableCell className="w-20">
        <DetailsDialog
          position={position}
          onPatch={(patch, optimistic) =>
            set({ ...request, ...patch }, optimistic)
          }
          disabled={disabled}
        />
      </ETableCell>
      <ETableCell className="w-24">
        <InspectionSheet
          workOrder={workOrder}
          position={position}
          // checking for processId === null disables inspection when
          // the work order is only part of a commitment and not in a
          // process. Should this not be sufficient anymore a more
          // (need for inspection outside of process) this should be
          // disabled with a flag.
          disabled={disabled || workOrder.processId === null}
        />
      </ETableCell>
      <ETableCell className="w-8">
        <DeletePositionDialog workOrder={workOrder} position={position} />
      </ETableCell>
    </ETableRow>
  );
}

function AhuSelectCell({
  position,
  workOrder,
}: {
  position: Position;
  workOrder: WorkOrder;
}) {
  const { data: airHandlingUnits } = useHtzAirHandlingUnitListQuery({
    customer: { active: true, values: [workOrder.customerId] },
  });
  const items = [
    ...(airHandlingUnits?.items ?? []).map((ahu) => ({
      label: ahu.name,
      value: ahu.id,
    })),
    { label: t("Anlagenübergreifend"), value: NullUUID },
  ];

  const [assign, { isLoading, error, reset }] =
    useHtzWorkOrderAssignPositionAhuMutation();
  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
    }
  }, [error, reset, toast]);

  const setAirHandlingUnit = (ahuId: string | null) => {
    if (isLoading) {
      return;
    }

    assign({
      id: workOrder.id,
      positionId: position.id,
      ahuId: ahuId ?? NullUUID,
    });
  };

  return (
    <ETableCellSelect
      label={t("RLT-Anlagen")}
      items={items}
      value={position.ahuId ?? ""}
      onValueChange={setAirHandlingUnit}
      disabled={isLoading || isImmutable(workOrder)}
      fieldName={positionFieldName(position, "airHandlingUnitId")}
    />
  );
}

function DetailsDialog({
  position,
  onPatch,
  disabled,
}: {
  position: Position;
  onPatch: (patch: PositionPatch, optimistic: Position) => void;
  disabled?: boolean;
}) {
  const totalPriceKey = positionFieldName(position, "totalPrice");
  const totalHoursKey = positionFieldName(position, "totalHours");
  const totalPrice = useFieldErrors(totalPriceKey);
  const totalHours = useFieldErrors(totalHoursKey);
  const { resetFields } = useErrValidationFailedContext();
  // The totals are derived from the unit count, price and hours. The dialog's
  // trigger stays mounted with the row, so the dialog watches them.
  useResetFieldsOnChange({
    [totalPriceKey]: position.totalPrice,
    [totalHoursKey]: position.totalHours,
  });
  const { hasErrors } = useHasFieldErrors(
    [
      "description",
      "unitCount",
      "unitPrice",
      "unitHours",
      "totalPrice",
      "totalHours",
    ].map((field) => positionFieldName(position, field)),
  );

  // Turning an inspection on or off changes which assessments are required.
  const onInspectionRequiredChange = (list: string, patch: PositionPatch) => {
    resetFields(positionFieldName(position, list));
    onPatch(patch, { ...position, ...patch });
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          aria-invalid={hasErrors || undefined}
          className={cn(hasErrors && "text-destructive")}
        >
          {t("Details")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Details")}</DialogTitle>
          <DialogDescription>{t("Details zur Position")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TextareaField
            value={position.description}
            onChange={(e) =>
              onPatch(
                { description: e.target.value },
                { ...position, description: e.target.value },
              )
            }
            fieldName={positionFieldName(position, "description")}
            disabled={disabled}
          />
          <div className="grid grid-cols-3 gap-4">
            <NumberInputField
              label={t("Anzahl")}
              number={position.unitCount}
              onNumberChange={(unitCount) =>
                onPatch({ unitCount }, updateUnitCount(position, unitCount))
              }
              fieldName={positionFieldName(position, "unitCount")}
              disabled={disabled}
            />
            <DecimalNumberInputField
              label={t("Einzelpreis")}
              value={position.unitPrice}
              onValueChange={(unitPrice) =>
                onPatch({ unitPrice }, updateUnitPrice(position, unitPrice))
              }
              fieldName={positionFieldName(position, "unitPrice")}
              disabled={disabled}
            />
            <div className="grid gap-1.5">
              <Label>{t("Gesamtpreis")}</Label>
              <div
                className={cn(
                  "flex h-9 items-center rounded-md border px-3 text-sm",
                  totalPrice.invalid && "border-destructive",
                )}
              >
                {position.totalPrice.toLocaleString("de", {
                  minimumFractionDigits: 2,
                })}{" "}
                &euro;
              </div>
              <FieldErrors errors={totalPrice.errors} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DecimalNumberInputField
              label={t("Stunden pro Einheit")}
              value={position.unitHours}
              onValueChange={(unitHours) =>
                onPatch({ unitHours }, updateUnitHours(position, unitHours))
              }
              fieldName={positionFieldName(position, "unitHours")}
              disabled={disabled}
            />
            <div className="grid gap-1.5">
              <Label>{t("Gesamtstunden")}</Label>
              <div
                className={cn(
                  "flex h-9 items-center rounded-md border px-3 text-sm",
                  totalHours.invalid && "border-destructive",
                )}
              >
                {position.totalHours.toLocaleString("de", {
                  minimumFractionDigits: 2,
                })}
              </div>
              <FieldErrors errors={totalHours.errors} />
            </div>
          </div>
          <div className="grid gap-2">
            <InspectionRequiredCheckbox
              label={t("Konstruktive Beurteilung nötig")}
              checked={position.structuralInspectionRequired}
              onCheckedChange={(structuralInspectionRequired) =>
                onInspectionRequiredChange("structuralAssessments", {
                  structuralInspectionRequired,
                })
              }
              disabled={disabled}
            />
            <InspectionRequiredCheckbox
              label={t("Visuelle Beurteilung nötig")}
              checked={position.visualInspectionRequired}
              onCheckedChange={(visualInspectionRequired) =>
                onInspectionRequiredChange("visualAssessments", {
                  visualInspectionRequired,
                })
              }
              disabled={disabled}
            />
            <InspectionRequiredCheckbox
              label={t("Mikrobiologische Inspektion nötig")}
              checked={position.microbialInspectionRequired}
              onCheckedChange={(microbialInspectionRequired) =>
                onInspectionRequiredChange("microbialAssessments", {
                  microbialInspectionRequired,
                })
              }
              disabled={disabled}
            />
            <InspectionRequiredCheckbox
              label={t("Fotodokumentation nötig")}
              checked={position.photoDocumentationRequired}
              onCheckedChange={(photoDocumentationRequired) =>
                onInspectionRequiredChange("photos", {
                  photoDocumentationRequired,
                })
              }
              disabled={disabled}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface InspectionRequiredCheckboxProps {
  label: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  disabled?: boolean;
}

export function InspectionRequiredCheckbox({
  label,
  checked,
  onCheckedChange,
  disabled,
}: InspectionRequiredCheckboxProps) {
  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={label}
        checked={checked}
        onCheckedChange={(state) => {
          switch (state) {
            case true:
            case false:
              onCheckedChange(state);
              break;
            default:
          }
        }}
        disabled={disabled}
      />
      <label
        htmlFor={label}
        className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {label}
      </label>
    </div>
  );
}
